import differenceInMonths from 'date-fns/differenceInMonths';
import { zonedTimeToUtc } from 'date-fns-tz';
import { handleFetchError } from '../../../src/shared/util/handleFetchError';
import log from '../util/log';
import {
  AnimalTypeNames,
  AnimalTypeOptions,
  CreatePetCommentRequestSfs,
  DeletePetProfileRequest,
  PetAvatarOptions,
  PetInterface,
  SterilizationOptions,
} from '../types/pet';

import fetcher from '../util/fetcher';
import { defaultAvatarUrls, PET_PROFILE_A_URL, SERVER_TIME_ZONE } from '../util/config';
import getImageUrl from '../util/getImageUrl';
import {
  ApiPetBreedSize,
  ApiPetPrescription,
  ApiPetProfile,
  ApiPetProfileGenderEnum,
  ApiPetProfileSizeEnum,
  ApiPetProfileStatusEnum,
} from './generated-client/PetProfile-Service-A.v1/models';
import {
  PetAttributeApi,
  PetProfileApi,
  UploadPetImageV2Request,
} from './generated-client/PetProfile-Service-A.v1/apis';
import { Configuration } from './generated-client/PetProfile-Service-A.v1/runtime';

const cfg = new Configuration({
  fetchApi: fetcher,
  basePath: PET_PROFILE_A_URL,
  middleware: [],
});
const petAttributesApi = new PetAttributeApi(cfg);
const petProfileApi = new PetProfileApi(cfg);

const handlePetProfileAError = (error: any, apiName: string) =>
  error.response ? handleFetchError(error.response, apiName) : handleFetchError(error, apiName);

export const updatePetProfile = (petId: number, payload: ApiPetProfile) =>
  new Promise((resolve, reject) => {
    petProfileApi
      .updatePetProfile({
        id: petId,
        apiPetProfile: payload,
      })
      .then(() => {
        resolve({});
      })
      .catch((error) => reject(handlePetProfileAError(error, 'updatePetProfile')));
  });

export const deletePetProfile = (petProfile: number, payload: DeletePetProfileRequest) =>
  new Promise((resolve, reject) => {
    petProfileApi
      .deletePetProfile({ id: petProfile, statusReason: payload.statusReason })
      .then(() => resolve({}))
      .catch((error) => reject(handlePetProfileAError(error, 'deletePetProfile')));
  });

export const createPetComment = (payload: CreatePetCommentRequestSfs) =>
  new Promise((resolve, reject) => {
    petProfileApi
      .createPetComment({ id: payload.profileId, apiPetComment: { comment: payload.comment } })
      .then(resolve)
      .catch((error) => {
        log.error('An error occurred while posting comment', error);
        reject(handleFetchError(error));
      });
  });

export const uploadImage = (body: UploadPetImageV2Request) =>
  new Promise<string>((resolve, reject) => {
    petProfileApi
      .uploadPetImageV2(body)
      .then((r) => {
        r.imageUri
          ? resolve(r.imageUri)
          : reject(handlePetProfileAError(new Error('no imageUri in response'), 'uploadImage'));
      })
      .catch((error) => reject(handlePetProfileAError(error, 'uploadImage')));
  });

export const getPetAvatarOptions = ({ queryKey: [_, petTypeId] }: { queryKey: [string, number] }) =>
  new Promise<PetAvatarOptions>((resolve, reject) => {
    petAttributesApi
      .getPetAvatars({ petTypeId })
      .then((avatars) => {
        resolve({
          avatars: avatars.map((avatar) => ({
            imageUrl: avatar.imageUri || '',
            id: avatar.id || '',
          })),
        });
      })
      .catch((error) => reject(handlePetProfileAError(error, 'getPetAvatarOptions')));
  });

export const getPet = ({ queryKey: [, petId] }: { queryKey: [any, number] }) =>
  new Promise<PetInterface>((resolve, reject) => {
    petProfileApi
      .getPetProfile({ id: petId })
      .then((data) => resolve(transformPetPetProfileA(data)))
      .catch((error) => {
        reject(handlePetProfileAError(error, 'getPet'));
        // rerouting at this level works but only happens AFTER the error page flashes
      });
  });

export const getPetWithOwnerName = ({ queryKey: [, petId] }: { queryKey: [any, number] }) =>
  new Promise<PetInterface>((resolve, reject) => {
    petProfileApi
      .getPetProfile({ id: petId, includeOwnerName: true })
      .then((data) => resolve(transformPetPetProfileA(data)))
      .catch((error) => reject(handlePetProfileAError(error, 'getPet')));
  });

export const getActivePets = ({ queryKey: [_] }: { queryKey: [string] }) =>
  new Promise<PetInterface[]>((resolve, reject) => {
    petProfileApi
      .getPetProfiles({ petStatus: ApiPetProfileStatusEnum.Active })
      .then((pets) => resolve(pets?.results.map((pet) => transformPetPetProfileA(pet))))
      .catch((error) => reject(handlePetProfileAError(error, 'getPets')));
  });

export const getInactivePets = ({ queryKey: [_] }: { queryKey: [string] }) =>
  new Promise<PetInterface[]>((resolve, reject) => {
    petProfileApi
      .getPetProfiles({ petStatus: ApiPetProfileStatusEnum.Inactive })
      .then((pets) => resolve(pets?.results.map((pet) => transformPetPetProfileA(pet))))
      .catch((error) => reject(handlePetProfileAError(error, 'getPets')));
  });

export const getPetPrescriptions = ({ queryKey: [, petId] }: { queryKey: [any, number] }) =>
  new Promise<ApiPetPrescription[]>((resolve, reject) => {
    petProfileApi
      .getPetPrescriptions({ id: petId })
      .then(resolve)
      .catch((error) => {
        log.error('An error occurred while get prescriptions', error);
        reject(handleFetchError(error));
      });
  });

export const transformSterilization = (sterilization: boolean | null | undefined) => {
  if (sterilization === true) {
    return SterilizationOptions.yes;
  } else if (sterilization === false) {
    return SterilizationOptions.no;
  } else {
    return SterilizationOptions.unknown;
  }
};

export const transformGenderOptionsFromALayer = (gender: ApiPetProfileGenderEnum) => {
  if (gender === ApiPetProfileGenderEnum.Male) {
    return 'MALE';
  } else if (gender === ApiPetProfileGenderEnum.Female) {
    return 'FMLE';
  } else {
    return 'UNKN';
  }
};

const transformDateToTimestamp = (stringDate: string): number =>
  zonedTimeToUtc(stringDate, SERVER_TIME_ZONE).getTime();

const transformPetPetProfileA = (pet: ApiPetProfile): PetInterface => {
  const birthday = pet.birthday ? transformDateToTimestamp(pet.birthday) : undefined;
  const petTypeName = pet.type?.name as AnimalTypeNames;
  const petTypeId = String(pet.type?.id) as AnimalTypeOptions;
  const petSize = pet.size ? pet.size : ApiPetProfileSizeEnum.Unknown;
  const breeds = pet.breeds || [
    { id: 0, name: '', size: ApiPetBreedSize.Unknown, type: { id: 0, name: '' } },
  ];
  const petBreedSize = breeds[0].size === ApiPetBreedSize.Unknown ? 'UNKNOWN_SIZE' : breeds[0].size;
  const derived = pet.derived;
  return {
    petTypeId,
    id: pet.id || 0,
    name: pet.name,
    petType: { ...pet.type, name: petTypeName },
    petBreedId: breeds[0].id,
    petBreedName: breeds[0].name,
    petBreedSize: petBreedSize,
    secondaryBreedId: breeds[1]?.id,
    secondaryBreedName: breeds[1]?.name,
    gender: transformGenderOptionsFromALayer(pet.gender || 'UNKNOWN'),
    weightLbs: pet.weight || undefined,
    lifestage: pet.lifestage || '',
    birthday,
    adoptionDate: pet.adoptionDate ? transformDateToTimestamp(pet.adoptionDate) : undefined,
    ageInMonths: birthday ? differenceInMonths(new Date(), birthday) : pet.age || undefined,
    avatarId: pet.avatar?.id || 0,
    avatarUrl: pet.avatar?.imageUri || defaultAvatarUrls[petTypeName],
    avatarSource: pet.avatar?.imageSource,
    photoUrl: pet?.bannerImageUri ? getImageUrl(pet.bannerImageUri, 150, 'AC_SS') : undefined,
    sizeType: petSize === ApiPetProfileSizeEnum.Unknown ? 'UNKNOWN_SIZE' : petSize,
    adopted: pet.adopted || false,
    status: pet.status,
    allergies: pet.allergies || [],
    medications: pet.medications || [],
    medicationAllergies: pet.medicationAllergies || [],
    medicalConditions: pet.medicalConditions || [],
    isSterilized: transformSterilization(pet.sterilization),
    derivedGender: derived?.genderAndSterilization,
    ownerName: pet.ownerName ? pet.ownerName : undefined,
    statusReason: pet.statusReason,
    ridingStyles: pet.ridingStyles || [],
    primaryDisciplines: pet.primaryDisciplines || [],
  };
};

const GET_ACTIVE_PETS = 'GET_ACTIVE_PETS';
const GET_INACTIVE_PETS = 'GET_INACTIVE_PETS';
const GET_PET_PRESCRIPTIONS = 'GET_PET_PRESCRIPTIONS';
const GET_PET = 'GET_PET';
const GET_PET_AVATAR_OPTIONS = 'GET_PET_AVATAR_OPTIONS';
const GET_PET_WITH_OWNER_NAME = 'GET_PET_WITH_OWNER_NAME';

getPetAvatarOptions.key = GET_PET_AVATAR_OPTIONS;

getActivePets.key = GET_ACTIVE_PETS;
getActivePets.requiresPetId = false;

getInactivePets.key = GET_INACTIVE_PETS;
getInactivePets.requiresPetId = false;

getPetPrescriptions.key = GET_PET_PRESCRIPTIONS;
getPetPrescriptions.requiresPetId = true;

getPet.key = GET_PET;
getPet.requiresPetId = true;

getPetWithOwnerName.key = GET_PET_WITH_OWNER_NAME;
getPetWithOwnerName.requiresPetId = true;
